import { useEffect, useState, type FormEvent } from 'react';
import { Package } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Switch } from '@/components/ui/Switch';
import { useCreateInventoryItem, useUpdateInventoryItem, type SaveItemInput } from '@/hooks/useInventory';
import {
  INVENTORY_KIND_LABELS, LOCATION_LABELS, VENEER_CUT_LABELS, VENEER_FIGURE_LABELS, VENEER_MATCH_LABELS,
  type InventoryItem, type InventoryKind, type Location,
  type VeneerCut, type VeneerFigure, type VeneerMatch, type UnitOfMeasure,
} from '@/types/inventory';

type Props = {
  open: boolean;
  onClose: () => void;
  item?: InventoryItem | null;
};

const UNITS: { value: UnitOfMeasure; label: string }[] = [
  { value: 'ea',     label: 'each' },
  { value: 'sf',     label: 'sq ft' },
  { value: 'sheet',  label: 'sheet' },
  { value: 'lf',     label: 'linear ft' },
  { value: 'gal',    label: 'gallon' },
  { value: 'roll',   label: 'roll' },
  { value: 'bundle', label: 'bundle' },
];

const EMPTY: SaveItemInput = {
  sku: '',
  name: '',
  kind: 'veneer',
  unit: 'bundle',
  active: true,
  fscCertified: true,
  fscClaim: 'FSC 100%',
};

export function ItemFormModal({ open, onClose, item }: Props) {
  const create = useCreateInventoryItem();
  const update = useUpdateInventoryItem();
  const editing = !!item;

  const [form, setForm] = useState<SaveItemInput>(EMPTY);
  const [initialQty, setInitialQty] = useState(0);
  const [initialLoc, setInitialLoc] = useState<Location>('receiving');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(item ? hydrate(item) : EMPTY);
      setInitialQty(0);
      setInitialLoc('receiving');
      setError(null);
    }
  }, [open, item]);

  function update_<K extends keyof SaveItemInput>(key: K, val: SaveItemInput[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const payload: SaveItemInput = {
        ...form,
        ...(editing
          ? {}
          : initialQty > 0
            ? { initialCount: { quantity: initialQty, location: initialLoc } }
            : {}),
      };

      if (editing && item) {
        await update.mutateAsync({ id: item.id, ...payload });
        toast.success(`Updated ${form.name}`);
      } else {
        await create.mutateAsync(payload);
        toast.success(`Added ${form.name}`);
      }
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Save failed.');
    }
  }

  const submitting = create.isPending || update.isPending;
  const isVeneer = form.kind === 'veneer';

  // Default unit suggestions per kind
  function onKindChange(k: InventoryKind) {
    const defaultUnit: UnitOfMeasure =
      k === 'veneer'    ? 'bundle' :
      k === 'substrate' ? 'sheet'  :
      k === 'finish'    ? 'gal'    :
      'ea';
    setForm((f) => ({
      ...f, kind: k,
      unit: f.unit === EMPTY.unit || f.unit === 'bundle' ? defaultUnit : f.unit,
    }));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker={editing ? 'Edit Item' : 'New Inventory Item'}
      title={editing ? item?.name ?? 'Item' : 'Add to inventory.'}
      description={editing ? 'Update catalog details. Stock levels are changed via movements on the detail page.' : 'Create a new SKU. Enter an initial count to seed stock.'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" form="item-form" loading={submitting}>
            {editing ? 'Save Changes' : 'Create Item'}
          </Button>
        </>
      }
    >
      <form id="item-form" onSubmit={onSubmit} className="space-y-6 py-2">
        {/* Identity */}
        <FieldGroup label="Identity">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="SKU"
              required
              placeholder="VEN-WAL-QTR-0412"
              value={form.sku}
              onChange={(e) => update_('sku', e.target.value.toUpperCase())}
              hint="Unique identifier. Uppercase recommended."
            />
            <Select
              label="Kind"
              value={form.kind}
              onChange={(e) => onKindChange(e.target.value as InventoryKind)}
              options={Object.entries(INVENTORY_KIND_LABELS).map(([v, l]) => ({ value: v, label: l }))}
            />
            <Input
              label="Name"
              required
              placeholder="Walnut, Quartered, Figured — Flitch 0412"
              value={form.name}
              onChange={(e) => update_('name', e.target.value)}
              leftIcon={<Package size={15} />}
              className="md:col-span-2"
            />
            <Textarea
              label="Description"
              rows={2}
              value={form.description ?? ''}
              onChange={(e) => update_('description', e.target.value)}
              className="md:col-span-2"
            />
          </div>
        </FieldGroup>

        {/* Units & pricing */}
        <FieldGroup label="Units & Pricing">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Unit"
              value={form.unit}
              onChange={(e) => update_('unit', e.target.value as SaveItemInput['unit'])}
              options={UNITS}
            />
            <Input
              label="Unit Cost"
              type="number" min="0" step="0.01"
              placeholder="0.00"
              value={form.unitCost ?? ''}
              onChange={(e) => update_('unitCost', e.target.value === '' ? undefined : Number(e.target.value))}
              hint="What we paid — for valuation."
            />
            <Input
              label="Unit Price"
              type="number" min="0" step="0.01"
              placeholder="0.00"
              value={form.unitPrice ?? ''}
              onChange={(e) => update_('unitPrice', e.target.value === '' ? undefined : Number(e.target.value))}
              hint="For quick-quote lookups."
            />
          </div>
        </FieldGroup>

        {/* Reorder */}
        <FieldGroup label="Reorder Policy">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Reorder Point"
              type="number" min="0" step="any"
              placeholder="0"
              value={form.reorderPoint ?? ''}
              onChange={(e) => update_('reorderPoint', e.target.value === '' ? undefined : Number(e.target.value))}
              hint="Alert when on-hand ≤ this."
            />
            <Input
              label="Reorder Qty"
              type="number" min="0" step="any"
              placeholder="0"
              value={form.reorderQty ?? ''}
              onChange={(e) => update_('reorderQty', e.target.value === '' ? undefined : Number(e.target.value))}
            />
            <Input
              label="Preferred Supplier"
              placeholder="Optional"
              value={form.preferredSupplier ?? ''}
              onChange={(e) => update_('preferredSupplier', e.target.value)}
            />
          </div>
        </FieldGroup>

        {/* Veneer-specific */}
        {isVeneer && (
          <FieldGroup label="Veneer Specification">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Species"
                placeholder="Walnut, White Oak, Figured Maple…"
                value={form.veneerSpecies ?? ''}
                onChange={(e) => update_('veneerSpecies', e.target.value)}
                required
              />
              <Input
                label="Flitch Number"
                placeholder="e.g. 0412"
                value={form.flitchNumber ?? ''}
                onChange={(e) => update_('flitchNumber', e.target.value)}
              />
              <Select
                label="Cut"
                value={form.veneerCut ?? ''}
                onChange={(e) => update_('veneerCut', e.target.value as VeneerCut || undefined)}
                options={[
                  { value: '', label: '— Select —' },
                  ...Object.entries(VENEER_CUT_LABELS).map(([v, l]) => ({ value: v, label: l })),
                ]}
              />
              <Select
                label="Figure"
                value={form.veneerFigure ?? ''}
                onChange={(e) => update_('veneerFigure', e.target.value as VeneerFigure || undefined)}
                options={[
                  { value: '', label: '— Select —' },
                  ...Object.entries(VENEER_FIGURE_LABELS).map(([v, l]) => ({ value: v, label: l })),
                ]}
              />
              <Select
                label="Match"
                value={form.veneerMatch ?? ''}
                onChange={(e) => update_('veneerMatch', e.target.value as VeneerMatch || undefined)}
                options={[
                  { value: '', label: '— Select —' },
                  ...Object.entries(VENEER_MATCH_LABELS).map(([v, l]) => ({ value: v, label: l })),
                ]}
              />
              <Input
                label="Sq Ft per Bundle"
                type="number" min="0" step="any"
                placeholder="24"
                value={form.sqFtPerBundle ?? ''}
                onChange={(e) => update_('sqFtPerBundle', e.target.value === '' ? undefined : Number(e.target.value))}
              />
            </div>
          </FieldGroup>
        )}

        {/* Dimensions (substrate, door, panel) */}
        {['substrate', 'door', 'panel'].includes(form.kind) && (
          <FieldGroup label="Dimensions & Grade">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input label="Dimensions" placeholder={"4' × 8'"} value={form.dimensions ?? ''} onChange={(e) => update_('dimensions', e.target.value)} />
              <Input label="Thickness" placeholder={'3/4"'} value={form.thickness ?? ''} onChange={(e) => update_('thickness', e.target.value)} />
              <Input label="Grade" placeholder="A1, AA…" value={form.grade ?? ''} onChange={(e) => update_('grade', e.target.value)} />
            </div>
          </FieldGroup>
        )}

        {/* FSC */}
        <FieldGroup label="FSC® Chain of Custody">
          <div className="space-y-4">
            <Switch
              checked={form.fscCertified ?? false}
              onChange={(v) => update_('fscCertified', v)}
              label="FSC® Certified"
              description="Talbert's chain-of-custody number is C014235."
            />
            {form.fscCertified && (
              <Input
                label="FSC Claim"
                placeholder="FSC 100%"
                value={form.fscClaim ?? ''}
                onChange={(e) => update_('fscClaim', e.target.value)}
                hint="FSC 100%, FSC Mix Credit, FSC Recycled, etc."
              />
            )}
          </div>
        </FieldGroup>

        {/* Initial count (only on create) */}
        {!editing && (
          <FieldGroup label="Initial Count (optional)">
            <p className="text-xs text-text-muted mb-3">
              If you know what's on hand right now, record it here — it'll be logged as an "initial count" movement.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={`Quantity (${form.unit})`}
                type="number" min="0" step="any"
                placeholder="0"
                value={initialQty || ''}
                onChange={(e) => setInitialQty(Number(e.target.value) || 0)}
              />
              <Select
                label="Location"
                value={initialLoc}
                onChange={(e) => setInitialLoc(e.target.value as Location)}
                options={Object.entries(LOCATION_LABELS).map(([v, l]) => ({ value: v, label: l }))}
              />
            </div>
          </FieldGroup>
        )}

        {/* Active switch (edit only) */}
        {editing && (
          <div className="pt-3 border-t border-border">
            <Switch
              checked={form.active}
              onChange={(v) => update_('active', v)}
              label="Active"
              description="Inactive items are hidden from the default list but retain history."
            />
          </div>
        )}

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}
      </form>
    </Modal>
  );
}

function hydrate(i: InventoryItem): SaveItemInput {
  return {
    sku: i.sku, name: i.name, kind: i.kind,
    description: i.description, tags: i.tags,
    unit: i.unit, unitCost: i.unitCost, unitPrice: i.unitPrice,
    reorderPoint: i.reorderPoint, reorderQty: i.reorderQty, preferredSupplier: i.preferredSupplier,
    veneerSpecies: i.veneerSpecies, veneerCut: i.veneerCut, veneerFigure: i.veneerFigure,
    veneerMatch: i.veneerMatch, flitchNumber: i.flitchNumber, sqFtPerBundle: i.sqFtPerBundle,
    fscCertified: i.fscCertified, fscClaim: i.fscClaim,
    dimensions: i.dimensions, thickness: i.thickness, grade: i.grade,
    active: i.active,
  };
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="kicker mb-3">{label}</div>
      {children}
    </div>
  );
}
